import Bugsnag, { NotifiableError } from '@bugsnag/js'
import styled from 'styled-components'
import { Trans } from 'react-i18next'
import { useEffect } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import Divider from 'shared/components/Divider'
import Image from 'shared/components/Image'
import Spinner from 'shared/components/Spinner'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { Button } from 'shared/components/Button'
import { CheckoutQA } from 'shared/dataAttributes'
import { FCLink } from 'shared/components/Links'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { OrderSummaryOptions } from 'checkout/panels/OrderSummary/OrderSummaryOptions'
import { OrderSummaryPriceBreakDown } from 'checkout/panels/OrderSummary/OrderSummaryPriceBreakDown'
import { PriceBreakdownLineItemType } from 'shared/enums/PriceBreakdownLineItemType'
import { RotatingCaret } from 'shared/components'
import { colors, fonts } from 'shared/lib'
import { t } from 'localization'
import { useActivateWantOrderMutation } from 'api/wantsApi'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { useOrderSummaryContext } from 'checkout/containers/OrderSummaryContextProvider'
import { useWindowSize } from 'shared/hooks/useWindowSize'

const TP = 'checkout.panels.OrderSummary.OrderSummary'

export const OrderSummary = () => {
  const router = useRouter()
  const tv = useTranslation()
  const {
    buyOrderRTK,
    isFeeBreakdownOpen,
    order,
    orderReadyForPurchase,
    orderSubmitting,
    setIsFeeBreakdownOpen,
    showSpinner,
    spinnerText,
  } = useOrderSummaryContext()
  const [activateWantOrder, { isLoading: placingOffer }] = useActivateWantOrderMutation()
  const { isMobile } = useWindowSize()
  const isUpgradeFtyEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_PDP_HIDE_FASTEST_TO_YOU)
  const isCheckoutMarketPricingEnabled = useFeatureFlag(FeatureFlag.WEB_FC_CHECKOUT_MARKET_PRICING)
  const isOffersEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_ENABLE_OFFERS)
  const klarnaDisablePlaceOrder = order?.billingInfo?.paymentType === 'klarna'
  const payPalv2DisablePlaceOrder = order?.billingInfo?.paymentType === 'paypal_v2'

  const orderSubtotal = order?.pricingBreakdown?.lineItems?.find(
    (lineItem) => lineItem?.type === PriceBreakdownLineItemType.SUBTOTAL,
  )
  const orderPriorityProcessing = orderSubtotal?.subItems?.find(
    (subItem) => subItem?.type === PriceBreakdownLineItemType.PRIORITY_PROCESSING,
  )
  const showFeeBreakdown =
    isCheckoutMarketPricingEnabled &&
    order?.isInstantShip &&
    orderSubtotal?.subItems?.length > 0 &&
    !!orderPriorityProcessing?.localizedAmount?.amount

  const showUpgradeFtyOptions =
    isUpgradeFtyEnabled &&
    !isMobile &&
    order?.product?.productTemplate?.id &&
    order?.product?.productTemplate?.productCategory === 'shoes' &&
    order?.product?.variantShoeCondition === 'new_no_defects'

  const isWantOrder = isOffersEnabled && order?.wantId
  const placingOfferSpinnerText = t(`${TP}.placingOffer`, 'Placing offer...')

  const handleCheckoutClick = () => {
    if (isOffersEnabled && order?.wantId && order?.number) {
      const wantId = order?.wantId
      const orderNumber = order?.number
      return activateWantOrder({ wantId })
        .unwrap()
        .then((activateWantOrderResponse) => {
          if (activateWantOrderResponse?.status && activateWantOrderResponse?.status === 'active') {
            router.push(`/checkout/${orderNumber}/success`, `/checkout/${orderNumber}/success`, {
              locale: router.locale,
            })
          }
        })
        .catch((error) => Bugsnag.notify(error as NotifiableError))
    }
    return buyOrderRTK()
  }

  useEffect(() => {
    if (orderReadyForPurchase) {
      sendTrackingEvent('CHECKOUT_VIEW', {
        exposure_type: 'new',
        fastest_to_you: order?.isInstantShip,
        request_type: 'rtk',
        step: 'review',
      })
    }
  }, [orderReadyForPurchase])

  return (
    <Spinner
      showSpinner={isWantOrder ? placingOffer : showSpinner}
      subText={t(`${TP}.spinnerSubText`, 'Please do not close browser!')}
      text={isWantOrder ? placingOfferSpinnerText : spinnerText}
    >
      <ProductSummary>
        <StyledImage
          alt={order?.product?.productTemplate?.name}
          data-qa={CheckoutQA.OrderSummaryShoeImage}
          height={77}
          src={order?.product?.mainPictureUrl}
          width={77}
        />
        <ProductDescription>
          <ProductName data-qa={CheckoutQA.OrderSummaryShoeName}>
            {order?.product?.productTemplate?.name}
          </ProductName>
          {order?.product?.sizeOption?.presentation && (
            <Flex>
              <Size data-qa={CheckoutQA.OrderSummaryShoeSize}>
                {t(`${TP}.productSize`, {
                  defaultValue: 'US Size {size}',
                  size: order?.product?.sizeOption?.presentation,
                })}
              </Size>
            </Flex>
          )}
          <ProductDetailsLineTwo>
            <Flex>
              <ProductDetails data-qa={CheckoutQA.OrderSummaryShoeColorway}>
                {order?.product?.productTemplate?.details}
              </ProductDetails>
            </Flex>
            {!isUpgradeFtyEnabled && (
              <>
                <ProductPrice data-qa={CheckoutQA.OrderSummaryShoePrice}>
                  {orderSubtotal?.formattedAmount}
                </ProductPrice>
                {showFeeBreakdown && (
                  <button
                    data-qa={CheckoutQA.OrderSummaryBreakdownToggle}
                    onClick={() => setIsFeeBreakdownOpen(!isFeeBreakdownOpen)}
                  >
                    <RotatingCaret color={colors.FC2_GREY} isOpen={isFeeBreakdownOpen} />
                  </button>
                )}
              </>
            )}
          </ProductDetailsLineTwo>
        </ProductDescription>
      </ProductSummary>
      {showFeeBreakdown && isFeeBreakdownOpen && (
        <FeePrices>
          {orderSubtotal?.subItems?.map((subItem) => (
            <>
              {subItem?.type === PriceBreakdownLineItemType.SUBTOTAL_TOTAL_PRICE && (
                <PriceDivider />
              )}
              <PriceLine key={subItem?.type}>
                <FeeDetailTitle data-qa={getDataQaLabel(subItem?.type)}>
                  {subItem?.label}
                </FeeDetailTitle>
                <OrderSummaryCopy data-qa={getDataQaAmount(subItem?.type)}>
                  {subItem?.formattedAmount}
                </OrderSummaryCopy>
              </PriceLine>
            </>
          ))}
        </FeePrices>
      )}
      {showUpgradeFtyOptions ? <OrderSummaryOptions /> : <Divider />}
      <OrderSummaryPriceBreakDown />
      <TermsAndConditions data-qa={CheckoutQA.PlaceOrderTermsAndConditionsText}>
        {isWantOrder ? (
          <Trans
            components={[
              <FCLink
                key="terms-conditions"
                href="/terms-conditions"
                newTab
                qaAttr={CheckoutQA.PlaceOrderTermsConditionsLink}
              />,
              <FCLink
                key="privacy"
                href="/privacy"
                newTab
                qaAttr={CheckoutQA.PlaceOrderPrivacyPolicyLink}
              />,
            ]}
            defaults='By clicking "Place Offer", I acknowledge that I have read and agree to the <0>Terms & Conditions</0> and the <1>Privacy Policy</1>.'
            i18nKey={`${TP}.acknowledgeOffer`}
            t={tv.t}
          />
        ) : (
          <Trans
            components={[
              <FCLink
                key="terms-conditions"
                href="/terms-conditions"
                newTab
                qaAttr={CheckoutQA.PlaceOrderTermsConditionsLink}
              />,
              <FCLink
                key="privacy"
                href="/privacy"
                newTab
                qaAttr={CheckoutQA.PlaceOrderPrivacyPolicyLink}
              />,
            ]}
            defaults='By clicking "place order", I acknowledge that I have read and agree to the <0>Terms & Conditions</0> and the <1>Privacy Policy</1>.'
            i18nKey={`${TP}.acknowledge`}
            t={tv.t}
          />
        )}
      </TermsAndConditions>
      <Button
        $fill
        buttonType="primary2"
        disabled={
          orderSubmitting ||
          !orderReadyForPurchase ||
          klarnaDisablePlaceOrder ||
          payPalv2DisablePlaceOrder ||
          placingOffer
        }
        onClick={handleCheckoutClick}
        qaAttr={CheckoutQA.PlaceOrderButton}
        type="button"
      >
        {isWantOrder ? t(`${TP}.placeOffer`, 'PLACE OFFER') : t(`${TP}.placeOrder`, 'PLACE ORDER')}
      </Button>
      {isWantOrder && (
        <>
          <CharedIfOfferAccepted>
            {t(
              `${TP}.chargedIfOfferAccepted`,
              'You will only be charged if your offer is accepted.',
            )}
          </CharedIfOfferAccepted>
          {order?.pricingBreakdown?.total?.localizedAmount?.currency !== 'USD' && (
            <TransactInUsd>
              {t(`${TP}.transactInUsd`, 'If accepted, your offer will transact in USD.')}
            </TransactInUsd>
          )}
        </>
      )}
    </Spinner>
  )
}

const getDataQaAmount = (type: string) => {
  switch (type) {
    case PriceBreakdownLineItemType.PRIORITY_PROCESSING:
      return CheckoutQA.OrderSummaryPriorityProcessingFeeAmount
    case PriceBreakdownLineItemType.PRODUCT_PRICE:
      return CheckoutQA.OrderSummaryListPriceAmount
    case PriceBreakdownLineItemType.SUBTOTAL_TOTAL_PRICE:
      return CheckoutQA.OrderSummaryTotalPriceAmount
    default:
      return ''
  }
}

const getDataQaLabel = (type: string) => {
  switch (type) {
    case PriceBreakdownLineItemType.PRIORITY_PROCESSING:
      return CheckoutQA.OrderSummaryPriorityProcessingFeeTag
    case PriceBreakdownLineItemType.PRODUCT_PRICE:
      return CheckoutQA.OrderSummaryListPriceTag
    case PriceBreakdownLineItemType.SUBTOTAL_TOTAL_PRICE:
      return CheckoutQA.OrderSummaryTotalPriceTag
    default:
      return ''
  }
}

const Flex = styled.div`
  display: flex;
`
const OrderSummaryCopy = styled.p`
  margin: 0;
`
const ProductSummary = styled(Flex)`
  align-items: center;
`
const ProductDetails = styled(OrderSummaryCopy)`
  ${fonts.BODY_TEXT}
`
const ProductDescription = styled.div`
  ${fonts.BODY_TEXT}
  flex-grow: 1;
`
const Size = styled(OrderSummaryCopy)`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_GREY};
`
const FeePrices = styled.section`
  background-color: ${colors.FC2_OFF_WHITE};
  margin: 20px 0;
  padding: 20px 20px 10px;
`
const FeeDetailTitle = styled.span`
  margin-right: 5px;
`
const ProductDetailsLineTwo = styled(Flex)`
  justify-content: space-between;
  margin-top: 5px;
`
const ProductPrice = styled(OrderSummaryCopy)`
  ${fonts.SUBTITLE_3}
`
const ProductName = styled(OrderSummaryCopy)`
  ${fonts.SUBTITLE_2}
  font-weight: bold;
  margin: 5px 0;
`
const TermsAndConditions = styled.p`
  ${fonts.BODY_TEXT}
  margin: 20px 0;
`
const StyledImage = styled(Image)<{ src: string }>`
  background-image: url(${({ src }) => src});
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  margin-right: 12px;
  max-width: 77px;
  min-height: 77px;
  object-fit: contain;
  width: 100%;
`
const PriceLine = styled(Flex)`
  align-items: flex-end;
  color: ${colors.FC2_DARK_GREY};
  justify-content: space-between;
  margin-bottom: 10px;
`
const PriceDivider = styled(Divider)`
  margin: 15px 0;
`
const CharedIfOfferAccepted = styled.p`
  margin: 30px 0px 20px 0px;
  text-align: center;
  color: ${colors.FC2_BLACK};
  ${fonts.SUBTITLE_3};
`

const TransactInUsd = styled.p`
  margin: 20px 0px;
  text-align: center;
  color: ${colors.FC2_BLACK};
  ${fonts.SUBTITLE_3};
`
